"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { WavesOverviewParams } from "@/types/waves.types";
import { ApiWavesOverviewType } from "@/generated/models/ApiWavesOverviewType";
import { commonApiFetch } from "@/services/api/common-api";
import { ApiWave } from "@/generated/models/ApiWave";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface UseWavesOverviewProps {
  readonly type: ApiWavesOverviewType;
  readonly limit?: number | undefined;
  readonly following?: boolean | undefined;
  /**
   * If true, fetch only direct message waves. If false, exclude them. Undefined -> no filter.
   */
  readonly directMessage?: boolean | undefined;
  readonly refetchInterval?: number | undefined;
}
export const useWavesOverview = t0 => {
  const $ = _c(40);
  const {
    type,
    limit: t1,
    following: t2,
    directMessage,
    refetchInterval: t3
  } = t0;
  const limit = t1 === undefined ? 20 : t1;
  const following = t2 === undefined ? false : t2;
  const refetchInterval = t3 === undefined ? Infinity : t3;
  let t4;
  if ($[0] !== directMessage) {
    t4 = directMessage !== undefined ? {
      direct_message: directMessage
    } : {};
    $[0] = directMessage;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== following || $[3] !== limit || $[4] !== t4 || $[5] !== type) {
    t5 = {
      limit,
      type,
      only_waves_followed_by_authenticated_user: following,
      ...t4
    };
    $[2] = following;
    $[3] = limit;
    $[4] = t4;
    $[5] = type;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const params = t5;
  const [lastErrorTimestamp, setLastErrorTimestamp] = useState(null);
  let t6;
  let t7;
  if ($[7] !== params) {
    t6 = [QueryKey.WAVES_OVERVIEW, params];
    t7 = async t8 => {
      const {
        pageParam
      } = t8;
      const queryParams = {
        limit: `${params.limit}`,
        offset: `${pageParam}`,
        type: params.type,
        only_waves_followed_by_authenticated_user: `${params.only_waves_followed_by_authenticated_user}`
      };
      if (params.direct_message !== undefined) {
        queryParams.direct_message = `${params.direct_message}`;
      }
      return await commonApiFetch({
        endpoint: "waves-overview",
        params: queryParams
      });
    };
    $[7] = params;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== params) {
    t8 = (_, allPages) => allPages.at(-1)?.length === params.limit ? allPages.flat().length : null;
    $[10] = params;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getDefaultQueryRetry(() => setLastErrorTimestamp(Date.now()));
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== refetchInterval || $[14] !== t6 || $[15] !== t7 || $[16] !== t8) {
    t10 = {
      queryKey: t6,
      queryFn: t7,
      initialPageParam: 0,
      getNextPageParam: t8,
      placeholderData: keepPreviousData,
      refetchInterval,
      ...t9
    };
    $[13] = refetchInterval;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const query = useInfiniteQuery(t10);
  let t11;
  if ($[18] !== query.data?.pages) {
    t11 = () => query.data?.pages.flat() ?? [];
    $[18] = query.data?.pages;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const getWaves = t11;
  let t12;
  if ($[20] !== getWaves) {
    t12 = getWaves();
    $[20] = getWaves;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const [waves, setWaves] = useState(t12);
  let t13;
  if ($[22] !== getWaves) {
    t13 = () => {
      setWaves(getWaves());
    };
    $[22] = getWaves;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== query.data) {
    t14 = [query.data];
    $[24] = query.data;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  if ($[26] !== lastErrorTimestamp || $[27] !== query) {
    t15 = () => {
      if (lastErrorTimestamp && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.fetchNextPage();
        }, 30000);
        return;
      }
      query.fetchNextPage();
    };
    $[26] = lastErrorTimestamp;
    $[27] = query;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const fetchNextPage = t15;
  let t16;
  if ($[29] !== lastErrorTimestamp || $[30] !== query) {
    t16 = () => {
      if (lastErrorTimestamp && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.refetch();
        }, 30000);
        return;
      }
      query.refetch();
    };
    $[29] = lastErrorTimestamp;
    $[30] = query;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const refetch = t16;
  let t17;
  if ($[32] !== fetchNextPage || $[33] !== query.hasNextPage || $[34] !== query.isFetching || $[35] !== query.isFetchingNextPage || $[36] !== query.status || $[37] !== refetch || $[38] !== waves) {
    t17 = {
      waves,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      hasNextPage: query.hasNextPage,
      fetchNextPage,
      status: query.status,
      refetch
    };
    $[32] = fetchNextPage;
    $[33] = query.hasNextPage;
    $[34] = query.isFetching;
    $[35] = query.isFetchingNextPage;
    $[36] = query.status;
    $[37] = refetch;
    $[38] = waves;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const returnValue = t17;
  return returnValue;
};